import { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GlobalDispatchContext } from '../context/GlobalStore';
import {
  TextField,
  Stack,
  Typography,
  Select,
  Button,
  Grid,
  MenuItem,
  Box,
  Divider,
} from '@mui/material';
import Layout from '../mui-components/Layout';
import {
  createSubmitForm,
  validateEmail,
  validatePhoneNum,
} from '../utils/helpers';
import FormDevices from '../mui-components/FormDevices';
import { formItems, shippingOptions, formAddress } from '../data/orderForm';
import { getLocation } from '../utils/addressAutocomplete';
import FormAddress from '../mui-components/FormAddress';
import ErrorGenericFallback from '../mui-components/ErrorGenericFallback';
import { pageTitles } from '../data/pageTitles';
import { orderErrMsg } from '../data/orderForm';
import { useGlobalContext } from '../context/GlobalStore';

import apiClient from '../utils/apiClient';
import ErrorAlertModal from '../mui-components/ErrorAlertModal';

export default function PlaceOrder() {
  document.title = pageTitles.placeOrder;
  const { state, setOrders } = useGlobalContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { copiedOrder } = location.state || {};

  const [latLong, setLatLong] = useState(null);

  const [form, setForm] = useState(() => {
    const initialForm = {
      fname: '',
      lname: '',
      phone: '',
      email: '',
      company: '',
      address1: '',
      city: '',
      state: '',
      pcode: null,
      ccode: 'US',
      shipping: shippingOptions[0].list[0].value,
      signature: shippingOptions[1].list[0].value,
    };
    if (copiedOrder) {
      return {
        ...initialForm,
        fname: copiedOrder.fname || '',
        lname: copiedOrder.lname || '',
        phone: copiedOrder.phone || '',
        email: copiedOrder.email || '',
        company: copiedOrder.company || '',
        address1: copiedOrder.address1 || '',
        address2: copiedOrder.address2 || '',
        city: copiedOrder.city || '',
        state: copiedOrder.state || '',
        pcode: copiedOrder.pcode || '',
        ccode: copiedOrder.ccode || 'US',
        // Handle backward compatibility for old orders
        shipping: copiedOrder.shipping === 'Standard' ? 'standard' : 
                  copiedOrder.shipping === '2-Day shipping' ? 'two-day' : 
                  copiedOrder.shipping || 'standard',
        signature: copiedOrder.signature === 'Yes' ? 'yes' : 
                   copiedOrder.signature === 'No' ? 'no' : 
                   copiedOrder.signature || 'yes',
      };
    }
    return initialForm;
  });

  const [devices, setDevices] = useState([{ sku: '', quantity: 1, name: '' }]);
  const [consumables, setConsumables] = useState([{ sku: '', quantity: 1, name: '' }]);
  const dispatch = useContext(GlobalDispatchContext);
  const [errors, setErrors] = useState(Array(13).fill(false));
  const [addressErrors, setAddressErrors] = useState(Array(formAddress.length).fill(false));
  const [prices, setPrices] = useState([]);
  const [consumablePrices, setConsumablePrices] = useState([]);
  const [sameErrs, setSameErrs] = useState([]);
  const [consumableSameErrs, setConsumableSameErrs] = useState([]);
  const [options, setOptions] = useState([]);
  const [orderError, setOrderError] = useState(false);
  const [orderErrMsgState, setOrderErrMsgState] = useState(orderErrMsg);
  const [isDeviceErrorModalOpen, setIsDeviceErrorModalOpen] = useState(false);

  const onChange = (e, i) => {
    const copy = [...errors];
    if (i < formItems.length) {
      switch (e.target.name) {
        case 'email':
          if (validateEmail(e.target.value)) {
            copy[i] = false;
            setErrors(copy);
          } else {
            copy[i] = true;
            setErrors(copy);
          }
          break;
        case 'phone':
          if (validatePhoneNum(e.target.value)) {
            copy[i] = false;
            setErrors(copy);
          } else {
            copy[i] = true;
            setErrors(copy);
          }
          break;
        default:
          break;
      }
    }
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const onAddressChange = (e, i) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const onChangeDevice = (e, i) => {
    const errCopy = [...sameErrs];
    const copy = [...devices];
    if (
      e.target.name === 'sku' &&
      devices.some((device, index) => device.sku === e.target.value && index !== i)
    ) {
      errCopy[i] = true;
      setSameErrs(errCopy);
      copy[i][e.target.name] = '';
      setDevices(copy);
    } else {
      errCopy[i] = false;
      setSameErrs(errCopy);

      copy[i][e.target.name] = e.target.value;
      setDevices(copy);
    }
  };

  const onChangeConsumable = (e, i) => {
    const errCopy = [...consumableSameErrs];
    const copy = [...consumables];
    if (
      e.target.name === 'sku' &&
      consumables.some((item, index) => item.sku === e.target.value && index !== i)
    ) {
      errCopy[i] = true;
      setConsumableSameErrs(errCopy);
      copy[i][e.target.name] = '';
      setConsumables(copy);
    } else {
      errCopy[i] = false;
      setConsumableSameErrs(errCopy);

      copy[i][e.target.name] = e.target.value;
      setConsumables(copy);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    dispatch({
      type: 'SET_IS_LOADING',
      payload: true,
    });

    // Check if any device with a SKU has an invalid quantity
    const hasInvalidDevices = devices.some(device => 
      device.sku && (!device.quantity || device.quantity < 1)
    );
    
    // Check if any consumable with a SKU has an invalid quantity
    const hasInvalidConsumables = consumables.some(consumable => 
      consumable.sku && (!consumable.quantity || consumable.quantity < 1)
    );

    const allProducts = [
      ...devices.filter(d => d.sku && d.quantity >= 1),
      ...consumables.filter(c => c.sku && c.quantity >= 1)
    ];

    // Check if at least one product is selected
    if (allProducts.length === 0) {
      dispatch({
        type: 'SET_IS_LOADING',
        payload: false, 
      });
      setIsDeviceErrorModalOpen(true);
      setOrderErrMsgState({
        title: 'Validation Error',
        msg: 'Please add at least one product to your order.',
        link: '',
        linkText: '',
      });
      return;
    }

    if (hasInvalidDevices || hasInvalidConsumables) {
      dispatch({
        type: 'SET_IS_LOADING',
        payload: false, 
      });
      setIsDeviceErrorModalOpen(true);
      setOrderErrMsgState({
        title: 'Validation Error',
        msg: 'Please ensure all products with a SKU selected have a quantity of at least 1.',
        link: '',
        linkText: '',
      });
      return;
    }

    const body = createSubmitForm(form, allProducts);
    placeOrder(body);
  };

  const placeOrder = async (orderDetails) => {
    try {
      const accountId = state.account.pylo_accounts.find(
        (a) => a.api_key === state.apiKey.key
      )['id'];

      const response = await apiClient.post(
        '/placeOrder',
        {
          order: orderDetails,
          account_id: accountId,
          action: 'place_order',
        },
        {
        retryOptions: { maxRetries: 0 }
        },
        true
      );

      setOrders([response].concat(state.orders));
      navigate('/placeOrder/' + response.order_number);
      dispatch({
        type: 'SET_IS_LOADING',
        payload: false,
      });
    } catch (e) {
      console.log('POST call failed: ', JSON.parse(e.response.body));
      dispatch({
        type: 'SET_IS_LOADING',
        payload: false,
      });
      if (e.response && e.response.statusCode === 403) {
        setOrderErrMsgState({
          title: 'Access Denied.',
          msg: 'You do not have permission to make requests against this API key.',
          link: '/orders',
          linkText: 'Go to order list',
        })
      }
      setOrderError(true);
    }
  }

  const setAddress = (event, value) => { 
    if (typeof value === 'string' && value) {
      const arr = value.split(',');
      const len = arr.length;
      if (len >= 5) { 
        setForm({
          ...form,
          address1: arr.slice(0, len - 4).join(',').trim(),
          city: arr[len - 4]?.trim() || '',
          state: arr[len - 3]?.trim() || '',
          pcode: arr[len - 2]?.trim() || '',
          ccode: 'US', 
        });
      } else {
         setForm({ ...form, address1: value });
      }
    } else {
      setForm({ 
        ...form, 
        address1: value || '',
      });
    }
  };

  const getOptions = async () => {
    if (form?.address1) {
      const res = await getLocation(form?.address1, latLong);
      if (res?.Results.length > 0) {
        setOptions(res.Results);
      } else {
        setOptions([]);
      }
    }
  };

  useEffect(() => {
    getOptions();
  }, [form.address1]);

  // Function to get products specifically of type 'device' or those without a type (for backward compatibility)
  const getDeviceProducts = () => {
    // Ensure necessary state parts are available
    if (!state?.account?.pylo_accounts || !state.apiKey?.key) {
      return {};
    }
    // Retrieve all product offerings for the current API key
    const allProducts = state.account.pylo_accounts.find(
      (a) => a.api_key === state.apiKey.key
    )?.product_offerings || {};
    
    // Filter for devices
    return Object.entries(allProducts)
      .filter(([_, product]) => !product.type || product.type === 'device')
      .reduce((obj, [sku, product]) => {
        obj[sku] = product;
        return obj;
      }, {});
  };

  // Function to get products of type 'consumable' or 'accessory'
  const getOtherProducts = () => {
    // Ensure necessary state parts are available
    if (!state?.account?.pylo_accounts || !state.apiKey?.key) {
      return {};
    }
    // Retrieve all product offerings for the current API key
    const allProducts = state.account.pylo_accounts.find(
      (a) => a.api_key === state.apiKey.key
    )?.product_offerings || {};

    const otherProductTypes = ['consumable', 'accessory'];
    // Filter for consumables and accessories
    return Object.entries(allProducts)
      .filter(([_, product]) => product.type && otherProductTypes.includes(product.type))
      .reduce((obj, [sku, product]) => {
        obj[sku] = product;
        return obj;
      }, {});
  };

  // Get the products to display in each section
  const deviceProductsToDisplay = getDeviceProducts();
  const otherProductsToDisplay = getOtherProducts();

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        setLatLong([position.coords.longitude, position.coords.latitude]);
      });
    }
  }, []);

  // This effect runs when the component loads and categorizes copied products
  useEffect(() => {
    const deviceProducts = getDeviceProducts();
    const otherProducts = getOtherProducts();

    if (copiedOrder && copiedOrder.products) {
      const devicesToCopy = [];
      const consumablesToCopy = [];

      copiedOrder.products.forEach(product => {
        if (deviceProducts.hasOwnProperty(product.sku)) {
          devicesToCopy.push(product);
        } else if (otherProducts.hasOwnProperty(product.sku)) {
          consumablesToCopy.push(product);
        }
      });

      if (devicesToCopy.length > 0) {
        setDevices(devicesToCopy);
      } else {
        setDevices([]); // Clear if no devices were copied
      }

      if (consumablesToCopy.length > 0) {
        setConsumables(consumablesToCopy);
      } else {
        setConsumables([]); // Clear if no consumables were copied
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copiedOrder]);

  return (
    <Layout title="Place Order" maxWidth={1200}>
      {orderError ? (
        <ErrorGenericFallback
          title={orderErrMsgState.title}
          msg={orderErrMsgState.msg}
          link={orderErrMsgState.link}
          linkText={orderErrMsgState.linkText}
        />
      ) : (
        <Box>
          <Stack
            gap={3}
            sx={{ width: '100%' }}
            component="form"
            onSubmit={handleSubmit}
            pb={3}
          >
            <Grid container spacing={2}>
              {formItems.map((item, i) => (
                <Grid
                  item
                  md={item.name !== 'company' ? 6 : 12}
                  key={`${item.name}-${i}`}
                >
                  <Typography variant="overline" color="text.primary">
                    {item.label}:{' '}
                  </Typography>
                  <TextField
                    value={form[item.name] || ''}
                    required={item.hasOwnProperty("required") ? item.required : true }
                    name={item.name}
                    placeholder={
                      item.name === 'phone' ? 'e.g. 111-111-1111' : ''
                    }
                    fullWidth
                    size="small"
                    onChange={(e) => onChange(e, i)}
                    error={errors[i]}
                    helperText={errors[i] ? 'Incorrect entry.' : ''}
                  />
                  {item.hasOwnProperty("subText") && (
                    <Typography variant="caption" color="textSecondary" sx={{ textAlign: 'left', maxWidth: 250 }}>
                      {item.subText}
                    </Typography>
                  )}
                </Grid>
              ))}
            </Grid>
            <FormAddress
              onChange={onAddressChange}
              options={options}
              errors={addressErrors}
              form={form}
              setAddress={setAddress}
            />
            <Grid container spacing={2}>
              {shippingOptions.map((opt, i) => (
                <Grid item sm={6} key={opt.label}>
                  <Typography variant="overline" color="text.primary">
                    {opt.label}:{' '}
                  </Typography>
                  <Select
                    fullWidth
                    size="small"
                    name={opt.name}
                    value={form[opt.name] || opt.list[0].value}
                    onChange={(e) => onChange(e)}
                  >
                    {opt.list.map((item, i) => (
                      <MenuItem value={item.value} key={`${opt.label}-${item.value}`}>
                        {item.display}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>Devices</Typography>
              <Divider sx={{ mb: 2, borderBottomWidth: '2px' }} />
              <FormDevices
                devices={devices}
                onChangeDevice={onChangeDevice}
                setDevices={setDevices}
                products={deviceProductsToDisplay}
                prices={prices}
                sameErrs={sameErrs}
                addButtonText="Add Device"
                startWithEmptyForm={true}
              />
            </Box>

            {/* Only show consumables section if there are consumable products */}
            {Object.keys(otherProductsToDisplay).length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Other Products</Typography>
                <Divider sx={{ mb: 2, borderBottomWidth: '2px' }} />
                <FormDevices
                  devices={consumables}
                  onChangeDevice={onChangeConsumable}
                  setDevices={setConsumables}
                  products={otherProductsToDisplay}
                  prices={consumablePrices}
                  sameErrs={consumableSameErrs}
                  addButtonText="Other Products"
                  startWithEmptyForm={true}
                />
              </Box>
            )}
            <Button variant="contained" type="submit" fullWidth>
              Submit Order
            </Button>
          </Stack>
          <ErrorAlertModal 
            open={isDeviceErrorModalOpen}
            handleClose={() => setIsDeviceErrorModalOpen(false)}
            title="Validation Error"
            message={orderErrMsgState.msg || "Please ensure all products with a SKU selected have a quantity of at least 1."}
          />
        </Box>
      )}
    </Layout>
  );
}
